// ============================================================
// avaliacaoCiclo.js — as perguntas da avaliação de encerramento do ciclo
//
// As perguntas são as do questionário "Avaliação de Encerramento do Ciclo
// 2025/2026", na mesma ordem e com as mesmas opções. Mudou uma coisa só: a
// seção 1 (nome, idade, data, modalidade) saiu. O atleta responde logado,
// e nome, modalidade, núcleo e data de nascimento já estão no cadastro; a
// data de preenchimento é a hora do envio. Campo que o sistema já sabe,
// digitado à mão, só produz grafia divergente na hora de somar.
//
// As perguntas vivem aqui, e não dentro da tela, por três motivos: a
// contagem do que falta responder é testável sem renderizar nada, o
// relatório da coordenação usa a mesma lista para rotular cada coluna, e
// o Excel e o PDF saem na ordem do questionário sem ninguém repetir essa
// ordem em três arquivos.
// ============================================================

// O ciclo avaliado. `CICLO` entra no id do documento, por isso não tem
// barra; `CICLO_ROTULO` é o que aparece na tela e no documento impresso.
export const CICLO = "2025-2026";
export const CICLO_ROTULO = "2025/2026";

// O prazo combinado com a coordenação.
//
// O prazo NÃO fecha o questionário. Quem passar do dia 9 continua
// respondendo e continua liberando o painel, e a resposta fica marcada
// como fora do prazo. Fechar no dia 9 criaria atleta sem caminho nenhum:
// o painel bloqueado para sempre e nenhuma tela para desbloquear.
export const PRAZO = "2026-10-09";

// A orientação que abre o questionário em papel, mantida palavra por
// palavra: é ela que diz que não há resposta certa, e isso muda o que o
// adolescente responde na pergunta sobre a equipe técnica.
export const ORIENTACAO =
  "Responda considerando sua participação neste ciclo. Marque uma alternativa por " +
  "pergunta e escreva suas opiniões nos campos abertos. Suas respostas ajudarão a " +
  "planejar o próximo ciclo. Não há respostas certas ou erradas.";

// Escalas repetidas. Escritas uma vez para que duas perguntas com a mesma
// escala não acabem com opções ligeiramente diferentes, o que quebraria a
// comparação entre as duas.
const QUANTO = ["Muita", "Moderada", "Pouca", "Nenhuma"];
const FREQUENCIA = ["Sempre", "Às vezes", "Raramente", "Nunca"];
const SIM_PARCIAL = ["Sim", "Parcialmente", "Não"];
const QUALIDADE_FEM = ["Muito boa", "Boa", "Regular", "Ruim"];
const APOIO = ["Excelente", "Bom", "Regular", "Ruim"];

// As seções, na ordem do papel. A numeração mostrada na tela começa em 1
// de novo, porque a seção 1 do papel era a identificação e ela não existe
// mais aqui: o atleta veria a tela abrir na seção 2 sem entender por quê.
export const SECOES = [
  { id: "estrutura", titulo: "Estrutura e organização" },
  { id: "treinamento", titulo: "Treinamento físico e técnico" },
  { id: "pessoal", titulo: "Desenvolvimento pessoal e motivação" },
  { id: "ambiente", titulo: "Ambiente e equipe" },
  { id: "portal", titulo: "Portal e registro de presença" },
  { id: "sugestoes", titulo: "Sugestões e mensagem final" },
];

// As perguntas. `opcoes` define pergunta fechada; `aberta` define texto
// livre. O `id` é o nome do campo gravado e não muda nunca: mudar um id
// desliga a resposta já gravada do rótulo dela.
//
// `neutras` lista as opções que não são juízo e sim ausência de
// experiência ("Não participei", "Não utilizei"). Elas existem para o
// relatório não pintar de vermelho quem simplesmente não fez preparação
// física, e para não entrarem na conta do que foi bem ou mal avaliado.
export const PERGUNTAS = [
  // ── Estrutura e organização ────────────────────────────────
  {
    id: "organizacao",
    secao: "estrutura",
    texto: "Como você avalia a organização geral do programa neste ciclo?",
    opcoes: ["Excelente", "Boa", "Regular", "Ruim"],
  },
  {
    id: "clarezaDaComunicacao",
    secao: "estrutura",
    texto: "Os horários e as atividades foram comunicados com clareza?",
    opcoes: SIM_PARCIAL,
  },
  {
    id: "comunicacaoEntreTodos",
    secao: "estrutura",
    texto: "Como avalia a comunicação entre atletas, equipe técnica e coordenação?",
    opcoes: QUALIDADE_FEM,
  },

  // ── Treinamento físico e técnico ───────────────────────────
  {
    id: "evolucaoFisica",
    secao: "treinamento",
    texto: "Você percebeu evolução no seu desempenho físico neste ciclo?",
    opcoes: QUANTO,
  },
  {
    id: "evolucaoTecnica",
    secao: "treinamento",
    texto: "Você percebeu evolução na parte técnica da sua modalidade?",
    opcoes: QUANTO,
  },
  {
    id: "treinosMotivadores",
    secao: "treinamento",
    texto: "Os treinos foram motivadores?",
    opcoes: FREQUENCIA,
  },
  {
    id: "treinoPreferido",
    secao: "treinamento",
    texto: "Qual foi o tipo de treino ou exercício de que você mais gostou?",
    aberta: true,
  },

  // ── Desenvolvimento pessoal e motivação ────────────────────
  {
    id: "disciplina",
    secao: "pessoal",
    texto: "O programa ajudou você a melhorar sua disciplina e seu foco?",
    opcoes: SIM_PARCIAL,
  },
  {
    id: "motivacao",
    secao: "pessoal",
    texto: "Como você avalia seu nível de motivação ao longo deste ciclo?",
    opcoes: ["Muito alto", "Alto", "Médio", "Baixo"],
  },
  {
    id: "crescimentoPessoal",
    secao: "pessoal",
    texto: "O programa contribuiu para seu crescimento pessoal fora do esporte?",
    opcoes: ["Muito", "Um pouco", "Não"],
  },

  // ── Ambiente e equipe ──────────────────────────────────────
  {
    id: "convivencia",
    secao: "ambiente",
    texto: "Como você avalia a convivência entre os atletas?",
    opcoes: QUALIDADE_FEM,
  },
  {
    id: "apoioEquipeTecnica",
    secao: "ambiente",
    texto: "Como avalia o apoio e o incentivo da equipe técnica da modalidade?",
    opcoes: APOIO,
  },
  // A preparação física é avaliada à parte da equipe da modalidade, e tem
  // "Não participei" porque nem todo atleta faz o treino físico. Sem essa
  // opção, quem não participou responderia "Ruim" ou deixaria em branco, e
  // os dois jeitos estragam a média de quem participou.
  {
    id: "apoioPreparacaoFisica",
    secao: "ambiente",
    texto: "Como avalia o apoio e o incentivo na preparação física?",
    opcoes: [...APOIO, "Não participei"],
    neutras: ["Não participei"],
  },
  {
    id: "respeitado",
    secao: "ambiente",
    texto: "Você se sentiu respeitado dentro do programa?",
    opcoes: FREQUENCIA,
  },
  {
    id: "espacoParaFalar",
    secao: "ambiente",
    texto: "Você teve espaço para expressar dúvidas, dificuldades e sugestões?",
    opcoes: FREQUENCIA,
  },

  // ── Portal e registro de presença ──────────────────────────
  {
    id: "facilidadeDoRegistro",
    secao: "portal",
    texto: "Como você avalia a facilidade para registrar sua presença no aplicativo ou portal?",
    opcoes: ["Muito fácil", "Fácil", "Difícil", "Muito difícil", "Não utilizei"],
    neutras: ["Não utilizei"],
  },
  {
    id: "orientacoesDeRegistro",
    secao: "portal",
    texto: "As orientações sobre registro de presença e justificativa de faltas foram claras?",
    opcoes: ["Sim", "Parcialmente", "Não", "Não recebi orientações"],
  },
  {
    id: "dificuldadeNoPortal",
    secao: "portal",
    texto: "Se teve dificuldade no aplicativo ou portal, descreva o que aconteceu.",
    aberta: true,
  },

  // ── Sugestões e mensagem final ─────────────────────────────
  // A seção que fecha o questionário é toda de escrever, e é a que a
  // coordenação lê uma a uma para montar o ciclo seguinte. Fica por último
  // de propósito: pedida no começo, a sugestão vem antes de o atleta
  // repassar o ano pergunta a pergunta, e vem mais pobre.
  {
    id: "sugestoesProximoCiclo",
    secao: "sugestoes",
    texto: "Alguma sugestão de treino, evento, competição ou melhoria para o próximo ciclo?",
    aberta: true,
  },
  {
    id: "mensagemFinal",
    secao: "sugestoes",
    texto: "Deixe sua mensagem final ou agradecimento para a equipe do programa.",
    aberta: true,
  },
];

// As fechadas são obrigatórias, as abertas não.
//
// Exigir as respostas escritas seria exigir redação de adolescente em fim
// de ciclo, e o que volta de uma obrigação dessas é ponto e traço. A
// pergunta fechada custa um toque e é ela que sustenta a comparação entre
// modalidades e entre ciclos. A última aberta é condicional no papel ("se
// teve dificuldade"), o que já a torna opcional por definição.
export const OBRIGATORIAS = PERGUNTAS.filter(p => !p.aberta).map(p => p.id);
export const ABERTAS = PERGUNTAS.filter(p => p.aberta).map(p => p.id);

// Acesso por id, para o relatório rotular uma resposta gravada sem varrer
// a lista inteira a cada célula da planilha.
const PORID = {};
PERGUNTAS.forEach(p => { PORID[p.id] = p; });
export function pergunta(id) {
  return PORID[id] || null;
}

// O que ainda falta para poder enviar. Devolve os ids na ordem da tela,
// para a tela poder levar o atleta até a primeira pendência.
export function faltamResponder(respostas) {
  const r = respostas || {};
  return OBRIGATORIAS.filter(id => !String(r[id] || "").trim());
}

// Perguntas de uma seção, na ordem em que foram declaradas.
export function perguntasDaSecao(secaoId) {
  return PERGUNTAS.filter(p => p.secao === secaoId);
}

// A opção é juízo ou é só ausência de experiência?
export function opcaoNeutra(perguntaId, opcao) {
  const p = PORID[perguntaId];
  return Boolean(p && Array.isArray(p.neutras) && p.neutras.includes(opcao));
}

// A resposta chegou dentro do prazo?
//
// Compara texto ISO em vez de Date: data montada com `new Date("2026-10-09")`
// nasce à meia-noite em UTC, que em Fortaleza ainda é dia 8, e o atleta que
// responde na noite do último dia apareceria como atrasado.
export function dentroDoPrazo(hojeISO, prazoISO = PRAZO) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(hojeISO || ""))) return false;
  return hojeISO <= prazoISO;
}

// "09/10/2026" a partir de "2026-10-09".
export function prazoEmBR(prazoISO = PRAZO) {
  const [a, m, d] = String(prazoISO).split("-");
  return `${d}/${m}/${a}`;
}

// Quantos dias faltam para o prazo. Negativo quando já passou, zero no
// próprio dia. Em dias corridos, contados ao meio-dia em UTC, para que o
// horário de verão de qualquer ano não transforme a conta em 23 horas.
export function diasAteOPrazo(hojeISO, prazoISO = PRAZO) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(hojeISO || ""))) return null;
  function emUTC(iso) {
    const [ano, mes, dia] = String(iso).split("-").map(Number);
    return Date.UTC(ano, mes - 1, dia, 12);
  }
  const UM_DIA = 86400000;
  return Math.round((emUTC(prazoISO) - emUTC(hojeISO)) / UM_DIA);
}

// A frase do prazo, do jeito que o atleta lê no alto da tela.
export function recadoDoPrazo(hojeISO, prazoISO = PRAZO) {
  const dias = diasAteOPrazo(hojeISO, prazoISO);
  const data = prazoEmBR(prazoISO);
  if (dias === null) return `O prazo para responder é ${data}.`;
  if (dias < 0) return `O prazo era ${data} e já passou. Responda agora para liberar o seu painel.`;
  if (dias === 0) return `Hoje é o último dia: o prazo para responder é ${data}.`;
  if (dias === 1) return `Falta 1 dia: o prazo para responder é ${data}.`;
  return `Faltam ${dias} dias: o prazo para responder é ${data}.`;
}

// ── Apuração, usada pelo relatório, pelo Excel e pelo PDF ────
//
// Uma função só, para os três saírem com os mesmos números. Quando a
// contagem morava na tela, o Excel refazia a conta do seu jeito e bastava
// uma diferença de arredondamento para a planilha e o painel discordarem
// na reunião.
//
// Devolve, para cada pergunta fechada, a contagem por opção na ordem
// declarada, e o percentual sobre quem respondeu AQUELA pergunta.
export function apurar(respostasDeTodos) {
  const lista = Array.isArray(respostasDeTodos) ? respostasDeTodos : [];
  const fechadas = PERGUNTAS.filter(p => !p.aberta).map(p => {
    const contagem = {};
    p.opcoes.forEach(o => { contagem[o] = 0; });
    let responderam = 0;
    lista.forEach(r => {
      const v = String(((r && r.respostas) || {})[p.id] || "").trim();
      if (!v) return;
      // Opção que não existe mais (questionário editado depois de alguém
      // responder) é contada à parte em vez de sumir: resposta somem é
      // pior do que resposta estranha, porque ninguém percebe.
      if (!(v in contagem)) contagem[v] = 0;
      contagem[v] += 1;
      responderam += 1;
    });
    return {
      id: p.id,
      texto: p.texto,
      secao: p.secao,
      responderam,
      opcoes: Object.keys(contagem).map(opcao => ({
        opcao,
        qtd: contagem[opcao],
        pct: responderam > 0 ? Math.round((contagem[opcao] / responderam) * 100) : 0,
        neutra: opcaoNeutra(p.id, opcao),
      })),
    };
  });

  const abertas = PERGUNTAS.filter(p => p.aberta).map(p => ({
    id: p.id,
    texto: p.texto,
    secao: p.secao,
    respostas: lista
      .map(r => ({
        nome: (r && r.nome) || "",
        modalidade: (r && r.modalidade) || "",
        texto: String(((r && r.respostas) || {})[p.id] || "").trim(),
      }))
      .filter(x => x.texto),
  }));

  return { fechadas, abertas, total: lista.length };
}
